import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {dialog, registerEvent} from '../../electron-modules';
import {store} from '../../redux/store';
import {
  getCameraFileAssets,
  getModelAssets,
  getPointCloudAssets,
  selectAllAssets
} from '../../redux/assets-slice';
import {trackToolUsage} from '../../executable';
import {useSelector} from 'react-redux';
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import {
  DenseDivider,
  EnhancedTable,
  LinearProgressWithLabel,
  SimpleDialog,
  TypographyLink
} from '../../components';
import sanitize from "sanitize-filename";
import {selectProjectID, selectProjectName} from '../../redux/project-slice';
import {toast} from '../../app';
import {useTranslation} from 'react-i18next';
import {useAuth, useDialog} from "../../hooks";
import {getCloudExporter} from "./exporter";
import {getLocalizedProjectName} from '../../utilities';
import {throttle} from 'throttle-debounce';
import {getLocalizedURL} from '../../localization';
import { engineCloudURL } from '../../urls';
import { useTableState } from '../../hooks/use-table-state';
import { NewProjectInfo } from '../common';
import { StatusCode } from '../../types/cloud-export';

interface AssetStatus {
  filename: string;
  status_code: number;
}

interface AssetStatuses {
  [key: string]: AssetStatus;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentCenter: {
      textAlign: "center"
    },
    cancelProgress: {
      marginRight: theme.spacing(1)
    },
    cancelContent: {
      paddingBottom: theme.spacing(3)
    }
  }),
);

interface LinkedProjectProps {
  name: string
}

const LinkedProjectInfo = (props: LinkedProjectProps) => {
  const {name} = props;

  const {t} = useTranslation();

  return (
    <React.Fragment>
      <Typography dangerouslySetInnerHTML={{
        __html: t("export.existing_project_updated", {
          name: name
        })
      }}/>
    </React.Fragment>
  );
};

const AssetStatusText = (props: {status: AssetStatus}) => {
  const {status} = props;

  const theme = useTheme();
  const {t} = useTranslation();

  const getColor = (status_code: StatusCode) => {
    switch (status_code) {
    case StatusCode.Uploaded:
      return theme.palette.success.main;
    case StatusCode.Uploading:
    case StatusCode.Preparing:
      return theme.palette.info.main;
    case StatusCode.Errored:
      return theme.palette.error.main;
    case StatusCode.Queued:
    default:
      return theme.palette.primary.main;
    }
  };

  const getText = (status_code: StatusCode) => {
    switch (status_code) {
    case StatusCode.Uploaded:
      return t(`export.uploaded`);
    case StatusCode.Uploading:
      return t(`export.uploading`);
    case StatusCode.Preparing:
      return t(`export.preparing`);
    case StatusCode.Errored:
      return t(`export.errored`);
    case StatusCode.Queued:
    default:
      return t(`export.queued`);
    }
  };

  return (
    <span style={{
      color: getColor(status.status_code)
    }}>
      {getText(status.status_code)}
    </span>
  );
};

export const CloudExporter = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {i18n, t} = useTranslation();

  const tableState = useTableState({
    defaultSort: "status_text",
    rowPropertyMap: {
      status_text: "status_code"
    }
  });

  const exporterDialog = useDialog();
  const messageDialog = useDialog();
  const cancelDialog = useDialog();

  const projectID = useSelector(selectProjectID);
  const projectName = useSelector(selectProjectName);
  const assets = useSelector(selectAllAssets);
  const pointClouds = getPointCloudAssets(assets);
  const camerasFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);

  const [assetStatuses, updateAssetStatuses] = useState<AssetStatuses>({});
  const [exportPercent, updateExportPercent] = useState(0);
  const [isExportRunning, setIsExportRunning] = useState(false);
  const [isExportFinished, setIsExportFinished] = useState(false);
  const [projectExists, setProjectExists] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [projectURL, setProjectURL] = useState("");

  const {linkAccount, loggedIn, getAppCode} = useAuth();
  const {startExport, cancelExport} = useMemo(getCloudExporter, []);

  const percentThrottleMilliseconds = 100;
  const setExportPercent = useCallback(throttle(
    percentThrottleMilliseconds, (value) => {
      updateExportPercent(value);
    }), []);

  const updateThrottleMilliseconds = 250;
  const setAssetStatuses = useCallback(throttle(
    updateThrottleMilliseconds, (value) => {
      updateAssetStatuses(value);
    }), []);

  const canExport = assets.length > 0;

  const getRows = (assetStatuses: AssetStatuses) => {
    return Object.keys(assetStatuses).map(key => {
      const assetStatus = assetStatuses[key];

      return {
        id: key,
        filename: assetStatus.filename,
        status_code: assetStatus.status_code,
        status_text: <AssetStatusText status={assetStatus}/>
      };
    });
  };

  const reset = () => {
    setExportPercent(0);
    setAssetStatuses({});
    setIsExportFinished(false);
    setProjectURL("");
    setHasErrors(false);
  };

  const onExportFinished = () => {
    setIsExportRunning(false);
    setIsExportFinished(true);
    setExportPercent(100);
  };

  const handleExportCancel = () => {
    reset();
    messageDialog.handleClose();
    cancelExport(projectExists);
  };

  const handleExport = () => {
    exporterDialog.handleClose();
    exportToCloud();
  };

  const checkProject = async (originalID): Promise<boolean> => {
    try {
      const url = `${engineCloudURL}/api/projects/${originalID}/exists`;
      const response = await fetch(url);
      return response.status === 200;
    } catch {
      return false;
    }
  };

  const exportToCloud = async() => {
    trackToolUsage("exporter");

    reset();
    messageDialog.handleOpen();

    const assets = [
      ...pointClouds,
      ...camerasFiles,
      ...modelFiles
    ];

    await startExport({
      name: exportName,
      assets: assets,
      update: projectExists,
      state: store.getState(),
      appCode: await getAppCode(true),
      onStart: setAssetStatuses,
      onUpdate: (statuses, error) => {
        setAssetStatuses({...statuses});
        if (error) setHasErrors(true);
      },
      onProgress: setExportPercent,
      onError: (title, message) => {
        onExportFinished();
        dialog.showErrorBox(title, message);
      },
      onFinish: (projectID) => {
        setProjectURL(getLocalizedURL(
          `${engineCloudURL}/viewer/${projectID}`, i18n.language));
        onExportFinished();
      }
    });
  };

  useEffect(() => {
    registerEvent("open-cloud-export", async () => {
      if (!canExport) return toast.error(t("toast.no-data-loaded"));

      if (!loggedIn) {
        const success = await linkAccount();
        if (!success) return;
      }

      const exists = await checkProject(projectID);
      setProjectExists(exists);
      exporterDialog.handleOpen();
    });
  }, [loggedIn, projectID, canExport]);

  const localizedProjectName = getLocalizedProjectName(projectName);
  const sanitizedName = sanitize(localizedProjectName);
  const exportName = t('export.export-name', {name: sanitizedName});

  let uploadMessage;

  if (projectExists) {
    uploadMessage = hasErrors
      ? t("export.project_updated_with_errors")
      : t("export.project-updated-successfully");
  } else {
    uploadMessage = hasErrors
      ? t("export.project_uploaded_with_errors")
      : t("export.project-uploaded-successfully");
  }

  const columns = [
    {
      id: 'filename',
      label: t("export.filename")
    },
    {
      id: 'status_text',
      label: t("export.status")
    }
  ];

  const rows = useMemo(() => {
    return getRows(assetStatuses);
  }, [assetStatuses]);

  return (
    <React.Fragment>
      {/* Exporting dialog */}
      <Dialog
        open={messageDialog.open}
        fullWidth
        scroll="body"
        maxWidth="sm"
      >
        <DialogTitle>{t('export.export-status')}</DialogTitle>

        <DialogContent className={classes.contentCenter}>
          {isExportFinished && (
            <>
              <Typography variant="body1">
                {uploadMessage}
              </Typography>

              <TypographyLink
                variant="caption"
                text={projectURL}
                url={projectURL}
              />

              <DenseDivider variant="middle" spacing={10}/>
            </>
          )}

          <LinearProgressWithLabel value={exportPercent} color="secondary"/>

          {(rows.length > 0) && (
            <div style={{
              paddingTop: theme.spacing(2)
            }}>
              <EnhancedTable
                state={tableState}
                columns={columns}
                rows={rows}
                maxHeight={"300px"}
              />
            </div>
          )}
        </DialogContent>

        <DialogActions>
          {!isExportFinished && (
            <Button
              onClick={handleExportCancel}
              color="primary"
              disabled={isExportRunning}
            >
              {t('export.cancel-export')}
            </Button>
          )}

          {isExportFinished && (
            <Button
              onClick={messageDialog.handleClose}
              color="primary"
            >
              {t("export.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Main dialog */}
      <SimpleDialog
        open={exporterDialog.open}
        onClose={exporterDialog.handleClose}
        onSubmit={handleExport}
        maxWidth={"xs"}
        title={t('export.data-export')}
        button={t("buttons.export")}
      >
        <Grid container spacing={2}>

          {/* Export file type(s) info */}
          <Grid item xs={12}>

            {/** Existing cloud project */}
            {projectExists && <LinkedProjectInfo
              name={sanitizedName}
            />}

            {/** New cloud project */}
            {!projectExists && <React.Fragment>
              <Typography dangerouslySetInnerHTML={{
                __html: t("export.new_project_will_be_created", {
                  name: sanitizedName
                })
              }}/>

              <NewProjectInfo/>
            </React.Fragment>}
          </Grid>
        </Grid>
      </SimpleDialog>

      {/* Cancelling dialog */}
      <Dialog
        open={cancelDialog.open}
      >
        <DialogTitle>{t('export.data-export')}</DialogTitle>
        <DialogContent className={classes.cancelContent}>
          <CircularProgress size="1em" className={classes.cancelProgress}/>
          <DialogContentText display="inline">
            {t('export.cancelling-export')}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};